/**
 * Home page analytics — the hero and the letters section — pushed to the
 * Google Tag Manager dataLayer already installed in app/layout.tsx
 * (GTM-TL3WCTB8).
 *
 * Analytics must never break the page. Every failure mode here — GTM absent,
 * blocked by an extension, not yet loaded — degrades to a no-op rather than
 * throwing inside a click handler and swallowing the navigation with it.
 */

import type { PhoneKey } from "@/lib/constants/contact";

/** The hero's aside slot holds offers when there are any, trust when there are not. */
export type HeroVariant = "offers" | "trust";

export type HeroEvent =
  | { event: "hero_cta_click"; locale: string }
  | {
      event: "hero_call_click";
      locale: string;
      /** Which business unit's line was tapped — mechanic, collision or parts. */
      line: PhoneKey;
    }
  | {
      event: "hero_offer_click";
      offerId: string;
      position: number;
      locale: string;
    }
  | { event: "hero_reviews_click"; locale: string }
  | {
      event: "hero_offers_rendered";
      offerIds: string[];
      count: number;
      /**
       * Which tenant of the hero's aside slot the visitor saw. `count: 0`
       * says no offers rendered; it does not say what stood in their place,
       * and without that, offer-period and trust-period traffic land in one
       * bucket that cannot be split apart afterwards.
       */
      variant: HeroVariant;
      locale: string;
    };

/**
 * Reach and engagement of the letters section. The view count is what makes
 * the expand count readable: few expands from few views is a placement
 * problem, few expands from many views is a content one.
 */
export type LettersEvent =
  | { event: "letters_section_view"; locale: string; letterId: string }
  | { event: "letter_expand"; locale: string; letterId: string };

export type SiteEvent = HeroEvent | LettersEvent;

export function trackEvent(payload: SiteEvent): void {
  if (typeof window === "undefined") return;
  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    // A non-array dataLayer (absent, or replaced by a hostile stub) is
    // clobbered with a fresh array so this and future events keep flowing,
    // rather than being dropped forever behind whatever was there.
    if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
    w.dataLayer.push(payload);
  } catch {
    // An ad blocker replacing dataLayer with a hostile stub must not cost
    // the visitor their click.
  }
}
